import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import DocumentManager from '../../../DocumentManager/DocumentManager';
import fileService from '../../../../../services/fileService';
import loadService from '../../../../../services/loadService';
import { transformLoadDataForAPI } from '../../loadDataTransformer';

const useDocumentSection = (formField, deletedField, options = {}) => {
  const { replaceOnUpload = false } = options;
  const { setValue, watch, getValues } = useFormContext();
  const formDocuments = watch(formField) || [];
  const [documents, setDocuments] = useState(formDocuments);
  const prevSignatureRef = useRef('');

  useEffect(() => {
    let isMounted = true;
    const signature =
      Array.isArray(formDocuments) && formDocuments.length > 0
        ? formDocuments
            .map((d) => (typeof d === 'string' ? d : d?.key ?? d?.id ?? d?.url ?? ''))
            .filter(Boolean)
            .join('|')
        : '';
    if (prevSignatureRef.current === signature) return;
    prevSignatureRef.current = signature;

    const normalize = async () => {
      if (formDocuments && Array.isArray(formDocuments)) {
        const normalized = formDocuments.map((doc, index) => {
          if (doc && typeof doc === 'object' && doc.id) return doc;
          if (typeof doc === 'string') {
            const key = doc.replace(/^\/api\/files\//, '').replace(/^\/files\//, '');
            return {
              id: `server-${index}-${key}`,
              name: key.split('/').pop() || 'document.pdf',
              size: 0,
              key,
              url: key,
            };
          }
          if (doc && typeof doc === 'object') {
            return {
              ...doc,
              id: doc.id || `doc-${index}-${Date.now()}`,
              key: doc.key || (doc.url && !doc.url.startsWith('http') ? doc.url : undefined),
              url: doc.url || doc.key || doc.url,
            };
          }
          return doc;
        }).filter(Boolean);

        const keysToSign = normalized
          .map((doc) => doc.key || doc.url)
          .filter((url) => url && typeof url === 'string' && !url.startsWith('http') && !url.startsWith('blob:'));

        if (keysToSign.length > 0) {
          const urlMap = await fileService.getSignedUrls(keysToSign);
          const withUrls = normalized.map((doc) => {
            const key = doc.key || doc.url;
            if (key && urlMap[key]) return { ...doc, url: urlMap[key] };
            return doc;
          });
          if (isMounted) setDocuments(withUrls);
        } else if (isMounted) {
          setDocuments(normalized);
        }
      } else if (isMounted) {
        setDocuments([]);
        prevSignatureRef.current = '';
      }
    };
    normalize();
    return () => { isMounted = false; };
  }, [formDocuments]);

  const handleUpload = useCallback((file) => {
    if (!file) return;
    const newDoc = {
      id: Date.now(),
      name: file.name,
      size: Math.max(1, Math.round(file.size / 1024)),
      file,
      url: URL.createObjectURL(file),
    };
    setDocuments((prev) => {
      const updated = replaceOnUpload ? [newDoc] : [...prev, newDoc];
      if (replaceOnUpload && prev.length > 0) {
        const existingKeys = prev
          .map((d) => d.key || (d.url && !d.url.startsWith('blob:') ? d.url : null))
          .filter(Boolean);
        if (existingKeys.length > 0) {
          const currentDeleted = getValues(deletedField) || [];
          setValue(deletedField, [...currentDeleted, ...existingKeys], { shouldDirty: true });
        }
      }
      setValue(formField, updated, { shouldDirty: true, shouldValidate: true });
      return updated;
    });
  }, [formField, deletedField, setValue, replaceOnUpload, getValues]);

  const handleRemove = useCallback((documentId) => {
    const docToRemove = documents.find((d) => d.id === documentId);
    if (docToRemove?.key && !docToRemove?.file) {
      const currentDeleted = watch(deletedField) || [];
      if (!currentDeleted.includes(docToRemove.key)) {
        setValue(deletedField, [...currentDeleted, docToRemove.key], { shouldDirty: true });
      }
    }
    if (docToRemove?.url && docToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(docToRemove.url);
    }
    const updated = documents.filter((d) => d.id !== documentId);
    setDocuments(updated);
    setValue(formField, updated, { shouldDirty: true, shouldValidate: true });
  }, [documents, deletedField, formField, setValue, watch]);

  return { documents, handleUpload, handleRemove };
};

const DocumentBlock = ({ title, formField, deletedField, documents, onUpload, onRemove, onGenerate, generateLabel, generateLoading, generateError }) => (
  <div className="documents-section-block">
    <h4 className="form-section-subtitle">{title}</h4>
    {generateError && (
      <div className="documents-generate-error">{generateError}</div>
    )}
    <DocumentManager
      documents={documents}
      onUpload={onUpload}
      onRemove={onRemove}
      onGenerate={onGenerate}
      generateLabel={generateLabel}
      hideGenerate={!onGenerate}
      generateLoading={generateLoading}
    />
  </div>
);

const DocumentsSection = ({ loadId }) => {
  const { getValues, setValue } = useFormContext();
  const bol = useDocumentSection('bolDocuments', 'deletedBolDocuments', { replaceOnUpload: true });
  const rateConf = useDocumentSection('rateConfirmationDocuments', 'deletedRateConfirmationDocuments', { replaceOnUpload: true });
  const other = useDocumentSection('documents', 'deletedDocuments');
  const [generateLoading, setGenerateLoading] = useState(null);
  const [generateError, setGenerateError] = useState({});

  const runGenerate = useCallback(async (type) => {
    const field = type === 'bol' ? 'bolDocuments' : 'rateConfirmationDocuments';
    setGenerateError((e) => ({ ...e, [type]: null }));
    setGenerateLoading(type);
    try {
      const values = getValues();
      const apiData = transformLoadDataForAPI(values);
      const res = type === 'bol'
        ? await loadService.generateBOL(apiData, loadId || undefined)
        : await loadService.generateRateConfirmation(apiData, loadId || undefined);
      const payload = res?.key != null ? res : (res?.data ?? res);
      if (!payload?.key) {
        setGenerateError((e) => ({ ...e, [type]: payload?.error || 'No document key returned' }));
        return;
      }
      const newDoc = {
        id: Date.now(),
        name: payload.key.split('/').pop() || (type === 'bol' ? 'BOL.pdf' : 'RateConfirmation.pdf'),
        size: 0,
        key: payload.key,
        url: payload.signedUrl || payload.key,
      };
      setValue(field, [newDoc], { shouldDirty: true, shouldValidate: true });
      if (payload.tempEntityId) {
        setValue('tempEntityId', payload.tempEntityId, { shouldDirty: true });
      }
    } catch (err) {
      const message = err.response?.data?.error || err.response?.data?.message || err.message || 'Generate failed';
      setGenerateError((e) => ({ ...e, [type]: message }));
    } finally {
      setGenerateLoading(null);
    }
  }, [loadId, getValues, setValue]);

  return (
    <div className="documents-section">
      <h3 className="form-section-title">Documents</h3>
      <DocumentBlock
        title="BOL"
        formField="bolDocuments"
        deletedField="deletedBolDocuments"
        documents={bol.documents}
        onUpload={bol.handleUpload}
        onRemove={bol.handleRemove}
        onGenerate={generateLoading ? undefined : () => runGenerate('bol')}
        generateLabel={generateLoading === 'bol' ? 'Generating…' : 'Generate BOL'}
        generateLoading={generateLoading === 'bol'}
        generateError={generateError.bol}
      />
      <DocumentBlock
        title="Rate Confirmation"
        formField="rateConfirmationDocuments"
        deletedField="deletedRateConfirmationDocuments"
        documents={rateConf.documents}
        onUpload={rateConf.handleUpload}
        onRemove={rateConf.handleRemove}
        onGenerate={generateLoading ? undefined : () => runGenerate('rateConfirmation')}
        generateLabel={generateLoading === 'rateConfirmation' ? 'Generating…' : 'Generate Rate Confirmation'}
        generateLoading={generateLoading === 'rateConfirmation'}
        generateError={generateError.rateConfirmation}
      />
      <DocumentBlock
        title="Other"
        formField="documents"
        deletedField="deletedDocuments"
        documents={other.documents}
        onUpload={other.handleUpload}
        onRemove={other.handleRemove}
      />
    </div>
  );
};

export default DocumentsSection;
